import React, { useMemo, useRef } from "react";

import { createColumnHelper } from "@tanstack/react-table";
import { twJoin } from "tailwind-merge";

import { Cell, Header, TableComp } from "src/base-components/Table";
import {
  CellDisplayType,
  getAllKeys,
  isStringCell,
  resolveColumnType,
  TABLE_COLUMN_MIN_WIDTH,
  TABLE_COLUMN_WIDTH,
} from "src/cases/caseView/blocks/cellTypes";
import { CellResizeHandle } from "src/cases/caseView/blocks/EditableTableCell";
import {
  ScrollableStringCell,
  TypedCellDisplay,
} from "src/cases/caseView/blocks/TypedCellDisplay";
import { useVerticalCellResize } from "src/cases/caseView/blocks/useVerticalCellResize";
import { TableBlockMultiColumnRecord } from "src/cases/queries/casesTypes";
import { TableColumnConfigOutput } from "src/clients/case-management";

// Read-mode string cell: the bottom handle drags the same per-cell height
// the edit mode uses, and the content scrolls inside the resulting box.
export const ReadModeStringCell: React.FC<{
  value: unknown;
  columnKey: string;
  displayType: CellDisplayType;
  userHeight?: number;
  onResize: (height: number) => void;
}> = ({ value, columnKey, displayType, userHeight, onResize }) => {
  const cellRef = useRef<HTMLDivElement>(null);
  const { isResizing, handleResizeStart } = useVerticalCellResize({
    cellRef,
    onHeightChange: onResize,
  });

  return (
    <div ref={cellRef} className="flex min-w-0 flex-1">
      {userHeight !== undefined ? (
        <ScrollableStringCell
          columnKey={columnKey}
          displayType={displayType}
          height={userHeight}
          value={value}
        />
      ) : (
        <TypedCellDisplay
          columnKey={columnKey}
          displayType={displayType}
          value={value}
        />
      )}
      <CellResizeHandle
        isResizing={isResizing}
        onResizeStart={handleResizeStart}
      />
    </div>
  );
};

const helper = createColumnHelper<Record<string, unknown>>();

type CellHeightAccessors = {
  getCellHeight: (rowIndex: number, columnKey: string) => number | undefined;
  onCellResize: (rowIndex: number, columnKey: string, height: number) => void;
};

const buildDynamicColumns = (
  keys: string[],
  columnsConfig: Record<string, TableColumnConfigOutput> | null | undefined,
  cellHeights: CellHeightAccessors,
) => {
  // The edit table's stable equal column sizing, so interactions and mode
  // switches never reflow columns.
  const columnSizing = {
    size: TABLE_COLUMN_WIDTH,
    minSize: TABLE_COLUMN_MIN_WIDTH,
    maxSize: TABLE_COLUMN_WIDTH,
  };

  return keys.map((key) => {
    const displayName = columnsConfig?.[key]?.display_name || key;
    return helper.accessor((row) => row[key], {
      id: key,
      ...columnSizing,
      header: (info) => (
        <Header dataLoc={`table-block-header-${key}`} info={info}>
          {displayName}
        </Header>
      ),
      cell: (info) => {
        const value = info.getValue();
        if (value === undefined) {
          return (
            <Cell
              className="truncate"
              dataLoc={`table-block-cell-${key}`}
              info={info}
            />
          );
        }

        const displayType = resolveColumnType(
          columnsConfig?.[key]?.display_config ?? null,
          value,
        );

        // String cells are resizable in read mode too: dragging sets the
        // same per-cell height that edit mode uses, and content scrolls
        // inside the resulting box.
        if (isStringCell(value, displayType)) {
          const userHeight = cellHeights.getCellHeight(info.row.index, key);
          return (
            <Cell
              className={twJoin(
                "group/table-cell relative contain-inline-size",
                userHeight !== undefined
                  ? "h-auto items-start py-0"
                  : "truncate",
              )}
              dataLoc={`table-block-cell-${key}`}
              info={info}
            >
              <ReadModeStringCell
                columnKey={key}
                displayType={displayType}
                userHeight={userHeight}
                value={value}
                onResize={(height) =>
                  cellHeights.onCellResize(info.row.index, key, height)
                }
              />
            </Cell>
          );
        }

        return (
          <Cell
            className="truncate contain-inline-size"
            dataLoc={`table-block-cell-${key}`}
            info={info}
          >
            <TypedCellDisplay
              columnKey={key}
              displayType={displayType}
              value={value}
            />
          </Cell>
        );
      },
    });
  });
};

export const MultiRecordDisplay: React.FC<{
  records: TableBlockMultiColumnRecord;
  columnsConfig?: Record<string, TableColumnConfigOutput> | null;
  getCellHeight: (rowIndex: number, columnKey: string) => number | undefined;
  onCellResize: (rowIndex: number, columnKey: string, height: number) => void;
}> = ({ records, columnsConfig, getCellHeight, onCellResize }) => {
  // Read through a ref so the column defs stay stable while a drag updates
  // heights every frame — rebuilt defs would remount the cell mid-drag.
  const accessorsValues = { getCellHeight, onCellResize };
  const accessors = useRef(accessorsValues);
  accessors.current = accessorsValues;

  const columns = useMemo(() => {
    const keys = getAllKeys(records);
    return buildDynamicColumns(keys, columnsConfig, {
      getCellHeight: (row, col) => accessors.current.getCellHeight(row, col),
      onCellResize: (row, col, height) =>
        accessors.current.onCellResize(row, col, height),
    });
  }, [records, columnsConfig]);

  return (
    <div className="flex h-full flex-col p-4">
      <div className="min-h-0 flex-1">
        <TableComp
          columns={columns}
          data={records}
          dataLoc="table-block-multi-record"
          disableVirtualization={true}
          frameClassName="h-full"
          getRowId={(_, index) => String(index)}
          rowClassName="align-top hover:bg-gray-50"
          variant="compact"
        />
      </div>
    </div>
  );
};
